import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ProductIcon = ({ type }: { type: string }): React.ReactElement => {
  const icons: Record<string, React.ReactElement> = {
    battery: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="b1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="bg"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <rect x="15" y="22" width="50" height="36" rx="6" fill="none" stroke="url(#b1)" strokeWidth="2" opacity="0.9" filter="url(#bg)"/>
        <rect x="15" y="22" width="50" height="36" rx="6" fill="#4A9EE8" opacity="0.08"/>
        <rect x="65" y="32" width="6" height="16" rx="3" fill="#4A9EE8" opacity="0.7"/>
        <rect x="18" y="25" width="28" height="30" rx="4" fill="#4A9EE8" opacity="0.25"/>
        <polygon points="42,32 34,44 39,44 32,58 50,42 44,42 52,32" fill="#7DBFF5" opacity="0.9" filter="url(#bg)"/>
        <circle cx="15" cy="22" r="3" fill="#7DBFF5" opacity="0.8"/>
        <circle cx="65" cy="22" r="3" fill="#4A9EE8" opacity="0.7"/>
        <circle cx="65" cy="58" r="3" fill="#4A9EE8" opacity="0.7"/>
        <circle cx="15" cy="58" r="3" fill="#7DBFF5" opacity="0.8"/>
      </svg>
    ),
    telecom: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="t1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="tg"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <line x1="40" y1="60" x2="40" y2="20" stroke="url(#t1)" strokeWidth="2.5" opacity="0.85" filter="url(#tg)"/>
        <path d="M20 38 Q40 20 60 38" fill="none" stroke="#4A9EE8" strokeWidth="2" opacity="0.6"/>
        <path d="M26 46 Q40 30 54 46" fill="none" stroke="#7DBFF5" strokeWidth="1.8" opacity="0.7"/>
        <path d="M32 54 Q40 42 48 54" fill="none" stroke="#4A9EE8" strokeWidth="1.5" opacity="0.5"/>
        <circle cx="40" cy="18" r="4" fill="#7DBFF5" opacity="0.9" filter="url(#tg)"/>
        <rect x="34" y="58" width="12" height="10" rx="2" fill="#4A9EE8" opacity="0.4"/>
        <circle cx="20" cy="38" r="2.5" fill="#4A9EE8" opacity="0.7"/>
        <circle cx="60" cy="38" r="2.5" fill="#4A9EE8" opacity="0.7"/>
      </svg>
    ),
    industrial: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="i1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="ig"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <rect x="12" y="30" width="56" height="30" rx="5" fill="none" stroke="url(#i1)" strokeWidth="2" opacity="0.85" filter="url(#ig)"/>
        <rect x="12" y="30" width="56" height="30" rx="5" fill="#4A9EE8" opacity="0.07"/>
        <rect x="18" y="36" width="12" height="18" rx="3" fill="#4A9EE8" opacity="0.25"/>
        <rect x="34" y="36" width="12" height="18" rx="3" fill="#4A9EE8" opacity="0.25"/>
        <rect x="50" y="36" width="12" height="18" rx="3" fill="#4A9EE8" opacity="0.25"/>
        <line x1="24" y1="20" x2="24" y2="30" stroke="#4A9EE8" strokeWidth="2" opacity="0.7"/>
        <line x1="40" y1="16" x2="40" y2="30" stroke="#7DBFF5" strokeWidth="2" opacity="0.8" filter="url(#ig)"/>
        <line x1="56" y1="20" x2="56" y2="30" stroke="#4A9EE8" strokeWidth="2" opacity="0.7"/>
        <circle cx="40" cy="14" r="4" fill="#7DBFF5" opacity="0.9" filter="url(#ig)"/>
        <circle cx="12" cy="30" r="2.5" fill="#4A9EE8" opacity="0.7"/>
        <circle cx="68" cy="30" r="2.5" fill="#4A9EE8" opacity="0.7"/>
      </svg>
    ),
    ess: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="e1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="eg"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <polygon points="40,8 68,24 68,56 40,72 12,56 12,24" fill="none" stroke="url(#e1)" strokeWidth="2" opacity="0.85" filter="url(#eg)"/>
        <polygon points="40,18 58,28 58,52 40,62 22,52 22,28" fill="#4A9EE8" opacity="0.1"/>
        <polygon points="40,28 52,35 52,49 40,56 28,49 28,35" fill="#4A9EE8" opacity="0.18"/>
        <circle cx="40" cy="40" r="8" fill="#4A9EE8" opacity="0.5"/>
        <circle cx="40" cy="40" r="4" fill="#7DBFF5" opacity="0.95" filter="url(#eg)"/>
        <circle cx="40" cy="8" r="3.5" fill="#7DBFF5" opacity="0.9"/>
        <circle cx="68" cy="24" r="2.5" fill="#4A9EE8" opacity="0.75"/>
        <circle cx="68" cy="56" r="2.5" fill="#4A9EE8" opacity="0.75"/>
        <circle cx="40" cy="72" r="3.5" fill="#7DBFF5" opacity="0.9"/>
        <circle cx="12" cy="56" r="2.5" fill="#4A9EE8" opacity="0.75"/>
        <circle cx="12" cy="24" r="2.5" fill="#4A9EE8" opacity="0.75"/>
      </svg>
    ),
    bms: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="bm1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="bmg"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <rect x="22" y="22" width="36" height="36" rx="5" fill="none" stroke="url(#bm1)" strokeWidth="2" opacity="0.9" filter="url(#bmg)"/>
        <rect x="22" y="22" width="36" height="36" rx="5" fill="#4A9EE8" opacity="0.08"/>
        <rect x="29" y="29" width="22" height="22" rx="3" fill="none" stroke="#7DBFF5" strokeWidth="1.5" opacity="0.6"/>
        <circle cx="40" cy="40" r="5" fill="#7DBFF5" opacity="0.9" filter="url(#bmg)"/>
        <line x1="29" y1="8" x2="29" y2="22" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="40" y1="8" x2="40" y2="22" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="51" y1="8" x2="51" y2="22" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="29" y1="58" x2="29" y2="72" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="40" y1="58" x2="40" y2="72" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="51" y1="58" x2="51" y2="72" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="8" y1="29" x2="22" y2="29" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="8" y1="51" x2="22" y2="51" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="58" y1="29" x2="72" y2="29" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="58" y1="51" x2="72" y2="51" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
      </svg>
    ),
    emobility: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="em1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="emg"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <ellipse cx="40" cy="40" rx="28" ry="20" fill="none" stroke="url(#em1)" strokeWidth="2" opacity="0.85" filter="url(#emg)"/>
        <ellipse cx="40" cy="40" rx="28" ry="20" fill="#4A9EE8" opacity="0.07"/>
        <circle cx="26" cy="52" r="8" fill="none" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.7"/>
        <circle cx="54" cy="52" r="8" fill="none" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.7"/>
        <circle cx="26" cy="52" r="3" fill="#7DBFF5" opacity="0.8"/>
        <circle cx="54" cy="52" r="3" fill="#7DBFF5" opacity="0.8"/>
        <polygon points="44,28 36,42 41,42 36,54 50,38 44,38 50,28" fill="#7DBFF5" opacity="0.9" filter="url(#emg)"/>
        <circle cx="40" cy="20" r="3" fill="#4A9EE8" opacity="0.7"/>
      </svg>
    ),
    cranking: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="cr1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="crg"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <rect x="10" y="25" width="60" height="32" rx="5" fill="none" stroke="url(#cr1)" strokeWidth="2" opacity="0.9" filter="url(#crg)"/>
        <rect x="10" y="25" width="60" height="32" rx="5" fill="#4A9EE8" opacity="0.08"/>
        <rect x="22" y="18" width="10" height="10" rx="3" fill="#4A9EE8" opacity="0.6"/>
        <rect x="48" y="18" width="10" height="10" rx="3" fill="#4A9EE8" opacity="0.6"/>
        <rect x="15" y="30" width="16" height="20" rx="3" fill="#4A9EE8" opacity="0.2"/>
        <rect x="35" y="30" width="10" height="20" rx="2" fill="#4A9EE8" opacity="0.15"/>
        <rect x="49" y="30" width="16" height="20" rx="3" fill="#4A9EE8" opacity="0.2"/>
        <line x1="27" y1="40" x2="27" y2="48" stroke="#7DBFF5" strokeWidth="2" opacity="0.8" filter="url(#crg)"/>
        <line x1="53" y1="40" x2="53" y2="48" stroke="#7DBFF5" strokeWidth="2" opacity="0.8" filter="url(#crg)"/>
        <circle cx="22" cy="18" r="2.5" fill="#7DBFF5" opacity="0.85"/>
        <circle cx="58" cy="18" r="2.5" fill="#7DBFF5" opacity="0.85"/>
      </svg>
    ),
    powertools: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="pt1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="ptg"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <rect x="18" y="18" width="44" height="28" rx="6" fill="none" stroke="url(#pt1)" strokeWidth="2" opacity="0.9" filter="url(#ptg)"/>
        <rect x="18" y="18" width="44" height="28" rx="6" fill="#4A9EE8" opacity="0.08"/>
        <rect x="30" y="46" width="20" height="16" rx="4" fill="none" stroke="#4A9EE8" strokeWidth="1.5" opacity="0.6"/>
        <rect x="30" y="46" width="20" height="16" rx="4" fill="#4A9EE8" opacity="0.12"/>
        <polygon points="42,24 35,35 39,35 33,48 48,34 43,34 50,24" fill="#7DBFF5" opacity="0.9" filter="url(#ptg)"/>
        <circle cx="18" cy="18" r="3" fill="#7DBFF5" opacity="0.85"/>
        <circle cx="62" cy="18" r="3" fill="#4A9EE8" opacity="0.75"/>
        <circle cx="62" cy="46" r="2.5" fill="#4A9EE8" opacity="0.7"/>
        <circle cx="18" cy="46" r="2.5" fill="#7DBFF5" opacity="0.75"/>
      </svg>
    ),
    marine: (
  <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
    <defs>
      <linearGradient id="mr1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#5DCAA5"/><stop offset="100%" stopColor="#4A9EE8"/></linearGradient>
      <filter id="mrg"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    </defs>
    <ellipse cx="40" cy="54" rx="26" ry="10" fill="none" stroke="url(#mr1)" strokeWidth="2" opacity="0.8" filter="url(#mrg)"/>
    <path d="M14 54 Q40 36 66 54" fill="none" stroke="#5DCAA5" strokeWidth="2" opacity="0.7"/>
    <path d="M20 50 Q40 38 60 50" fill="none" stroke="#7DBFF5" strokeWidth="1.5" opacity="0.5"/>
    <rect x="30" y="20" width="20" height="36" rx="5" fill="none" stroke="url(#mr1)" strokeWidth="2" opacity="0.9" filter="url(#mrg)"/>
    <rect x="30" y="20" width="20" height="20" rx="4" fill="#1D9E75" opacity="0.2"/>
    <rect x="35" y="8" width="5" height="14" rx="2.5" fill="#5DCAA5" opacity="0.85" filter="url(#mrg)"/>
    <rect x="44" y="8" width="5" height="14" rx="2.5" fill="#4A9EE8" opacity="0.75"/>
    <circle cx="40" cy="40" r="5" fill="#5DCAA5" opacity="0.9" filter="url(#mrg)"/>
    <circle cx="30" cy="20" r="2.5" fill="#5DCAA5" opacity="0.8"/>
    <circle cx="50" cy="20" r="2.5" fill="#4A9EE8" opacity="0.7"/>
  </svg>
),
  };
  return icons[type] || icons['battery'];
};

// Hook to get responsive visible count
function useVisibleCount() {
  const [visibleCount, setVisibleCount] = useState(4);

  useEffect(() => {
    const update = () => {
      if (window.innerWidth < 640) setVisibleCount(1);
      else if (window.innerWidth < 1024) setVisibleCount(2);
      else setVisibleCount(4);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return visibleCount;
}

export default function Services() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [currentIndex, setCurrentIndex] = useState(0);
  const visibleCount = useVisibleCount();

  const products = [
{ 
  icon: 'marine', 
  title: isRTL ? 'وحدة بطارية بحرية بالحالة الصلبة' : 'Solid-State Marine Battery Module', 
  desc: isRTL 
    ? 'وحدات بطاريات LiFePO₄ بالحالة الصلبة مصممة للتطبيقات البحرية والدفاعية عالية الأداء. متوفرة بتهيئات 12 فولت و24 فولت و48 فولت بسعة قابلة للتخصيص، وحماية IP67، وتيار ذروة يصل إلى 2000 أمبير — لضمان أداء موثوق ومستقر حرارياً للقوارب واليخوت والأنظمة البحرية والعسكرية.' 
    : 'Advanced solid-state LiFePO₄ battery modules engineered for high-performance marine and defense applications. Available in 12V, 24V, and 48V configurations with customisable capacity, IP67 protection, and up to 2000A peak current — delivering reliable, thermal-stable performance for boats, yachts, naval systems, and military applications.', 
  cat: isRTL ? 'البحرية والدفاع' : 'Marine & Defense', 
  premium: true 
},    { icon: 'battery', title: t('services.evaluation'), desc: t('services.evaluationDesc'), cat: 'LiFePO4' },
    { icon: 'telecom', title: t('services.dueDiligence'), desc: t('services.dueDiligenceDesc'), cat: 'Telecom' },
    { icon: 'industrial', title: t('services.activeManagement'), desc: t('services.activeManagementDesc'), cat: 'Industrial' },
    { icon: 'ess', title: t('services.valueCreation'), desc: t('services.valueCreationDesc'), cat: 'ESS' },
    { icon: 'bms', title: t('services.corporateServices'), desc: t('services.corporateDesc'), cat: 'Smart' },
    { icon: 'emobility', title: 'BMS Integration Solutions', desc: 'Advanced Battery Management System (BMS) integration solutions designed for real-time monitoring, diagnostics, and intelligent battery protection. Our systems provide complete visibility and control over battery performance, ensuring safe, stable, and efficient operation across telecom, industrial, ESS, and mobility applications.', cat: 'E-Mobility' },
    { icon: 'cranking', title: 'Cranking Batteries', desc: 'High-performance lithium cranking batteries designed for automotive and heavy-duty starting applications, delivering peak current up to 2000A for reliable engine starting. Available in customizable 12V and 24V systems with lightweight design, fast recharge capability, long lifecycle, and smart BMS protection for stable and efficient performance across various automotive applications.', cat: 'Cranking' },
    { icon: 'powertools', title: 'Power Tools Battery', desc: 'High-performance lithium-ion battery solutions designed for professional power tools, offering customizable voltage and capacity based on application requirements. Built with advanced Max Lithium Li-Ion technology for high power output, fast charging, long runtime, and reliable performance in demanding industrial and professional environments.', cat: 'Power Tools' },
  ];

const maxIndex = Math.max(0, products.length - visibleCount);

  // Reset index when visibleCount changes (e.g. resize)
  useEffect(() => {
    setCurrentIndex(0);
  }, [visibleCount]);

  const step = 1;
const prev = () => setCurrentIndex(i => Math.max(i - step, 0));
const next = () => setCurrentIndex(i => Math.min(i + step, maxIndex));

  return (
    <section id="services" className="py-16 md:py-32 relative overflow-hidden" style={{ backgroundColor: 'rgba(3,8,16,0.40)' }} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: `radial-gradient(circle at 25% 50%, #4A9EE8 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
      </div>
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl" />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 relative">

        {/* Header */}
        <div className="text-center mb-10 md:mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-px bg-gold" />
            <span className="text-gold text-sm font-medium tracking-widest uppercase">{t('services.label')}</span>
            <div className="w-12 h-px bg-gold" />
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
            {t('services.title1')} <span className="text-gold" style={{ textShadow: '0 0 30px rgba(74,158,232,0.4)' }}>{t('services.title2')}</span>
          </h2>
          <p className="text-base sm:text-lg text-white/50 max-w-2xl mx-auto px-4">{t('services.description')}</p>
        </div>

        {/* Slider */}
        <div className="relative overflow-hidden mb-8">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{
              gap: visibleCount === 1 ? '0px' : '1rem',
              // each card = (100% - gaps) / visibleCount, step = card + gap
              // REPLACE:
transform: (() => {
  const dir = isRTL ? 1 : -1;
  if (visibleCount === 1) {
    return `translateX(calc(${dir} * ${currentIndex} * 100%))`;
  }
  return `translateX(calc(${dir} * ${currentIndex} * (calc((100% - ${visibleCount - 1}rem) / ${visibleCount}) + 1rem)))`;
})(),
            }}
          >
            {products.map((product, index) => (
  <div
    key={index}
    className="group flex-shrink-0 rounded-2xl overflow-hidden flex flex-col"
    style={{
      backgroundColor: product.premium ? 'rgba(4,14,30,0.95)' : 'rgba(8,20,40,0.85)',
      width: visibleCount === 1 ? '100%' : `calc((100% - ${visibleCount - 1}rem) / ${visibleCount})`,
      border: product.premium ? '1.5px solid rgba(29,158,117,0.55)' : '1.5px solid rgba(74,158,232,0.5)',
      boxShadow: product.premium
        ? '0 0 20px rgba(29,158,117,0.15), 0 0 40px rgba(74,158,232,0.08), inset 0 0 20px rgba(29,158,117,0.04)'
        : '0 0 12px rgba(74,158,232,0.15), inset 0 0 20px rgba(74,158,232,0.04)',
    }}
  >
    {/* Replace the single top gradient line with this: */}
    <div className="h-0.5 w-full" style={{
      background: product.premium
        ? 'linear-gradient(to right, transparent, #1D9E75, #4A9EE8, #7DBFF5, transparent)'
        : 'linear-gradient(to right, transparent, #4A9EE8, transparent)'
    }} />

    <div className="p-5 sm:p-8 flex flex-col flex-1">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          
          <span className="px-3 py-1 text-xs font-medium rounded-full"
            style={{ backgroundColor: 'rgba(74,158,232,0.1)', color: product.premium ? '#5DCAA5' : '#4A9EE8', border: `1px solid ${product.premium ? 'rgba(29,158,117,0.25)' : 'rgba(74,158,232,0.2)'}` }}>
            {product.cat}
          </span>
        </div>
<div className="flex flex-col items-end gap-1">
  {product.premium && (
    <span style={{ color: '#5DCAA5', fontSize: '16px', lineHeight: 1, filter: 'drop-shadow(0 0 6px rgba(29,158,117,0.7))' }}>★</span>
  )}
  <span className="text-white/20 text-xs font-mono">{String(index + 1).padStart(2, '0')}</span>
</div>
      </div>

      <div className="w-16 h-16 sm:w-20 sm:h-20 mb-5 p-3 rounded-2xl" style={{
        backgroundColor: product.premium ? 'rgba(29,158,117,0.12)' : 'rgba(74,158,232,0.12)',
        boxShadow: product.premium ? 'inset 0 1px 0 rgba(93,202,165,0.2)' : 'inset 0 1px 0 rgba(255,255,255,0.1)',
        border: product.premium ? '1px solid rgba(29,158,117,0.2)' : undefined,
      }}>
        <ProductIcon type={product.icon} />
      </div>

      {/* rest of card content stays the same */}

                  <h3 className="text-lg sm:text-xl mb-3 leading-tight font-semibold text-white" style={{ fontFamily: "'Optima', 'Century Gothic', sans-serif", letterSpacing: '0.02em' }}>
                    {product.title}
                  </h3>

                  <p className="flex-1 text-sm sm:text-base" style={{ color: 'rgba(255,255,255,0.65)', fontFamily: "'Optima', 'Segoe UI', sans-serif", lineHeight: '1.75' }}>
                    {product.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-6 sm:gap-8">
          {/* Dot indicators */}
          <div className="flex gap-2">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
  <button
    key={i}
    onClick={() => setCurrentIndex(i)}
    className={`h-2.5 rounded-full transition-all duration-300 ${
      currentIndex === i
        ? 'bg-gold w-8 sm:w-10'
        : Math.abs(currentIndex - i) <= 1
          ? 'bg-white/50 w-3 sm:w-4 hover:bg-white/70'
          : 'bg-white/20 w-2.5 sm:w-3 hover:bg-white/40'
    }`}
  />
))}
          </div>

          {/* Arrow buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={prev}
              disabled={currentIndex === 0}
              className="w-11 h-11 sm:w-14 sm:h-14 rounded-full border-2 border-gold/40 flex items-center justify-center text-gold hover:bg-gold hover:border-gold hover:text-white transition-all duration-300 disabled:opacity-25 disabled:cursor-not-allowed"
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <button
              onClick={next}
              disabled={currentIndex === maxIndex}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-gold/20 flex items-center justify-center text-white hover:bg-gold hover:border-gold hover:text-white transition-all duration-300 disabled:opacity-25 disabled:cursor-not-allowed"
            >
              <i className="ri-arrow-right-line text-lg" />
            </button>
          </div>
        </div>

        

        {/* Explore button */}
        <div className="flex justify-center mt-10 sm:mt-12">
          <Link
            to="/products"
            className="inline-flex items-center gap-3 px-8 sm:px-10 py-3 sm:py-4 bg-gold text-white font-semibold text-sm sm:text-base rounded-lg transition-all duration-300 hover:shadow-xl group"
            style={{ boxShadow: '0 0 28px rgba(74,158,232,0.25)' }}
          >
            Explore Our Products
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

      </div>
    </section>
  );
}
